import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['createButton', 'panel']

  static values = {
    createStop: { type: Number, default: 0.5 },
    sensitivity: { type: Number, default: 1 },
    threshold: { type: Number, default: 8 },
    lock: { type: Boolean, default: false },
  }

  connect() {
    this.lastScrollY = this.currentScrollY()
    this.progress = 0
    this.travelPx = 1
    this.rafId = null
    this.needsRender = false
    this.isTransitioning = false

    this.boundHandleScroll = this.handleScroll.bind(this)
    this.boundRecalculateTravel = this.recalculateTravel.bind(this)
    this.boundStartTransition = this.startTransition.bind(this)
    this.boundEndTransition = this.endTransition.bind(this)

    window.addEventListener('scroll', this.boundHandleScroll, { passive: true })
    window.addEventListener('resize', this.boundRecalculateTravel)
    window.addEventListener('orientationchange', this.boundRecalculateTravel)
    window.visualViewport?.addEventListener('resize', this.boundRecalculateTravel)
    document.addEventListener('turbo:before-render', this.boundStartTransition)
    document.addEventListener('turbo:render', this.boundEndTransition)
    document.addEventListener('turbo:load', this.boundEndTransition)

    this.recalculateTravel()
    this.queueRender()
  }

  disconnect() {
    window.removeEventListener('scroll', this.boundHandleScroll)
    window.removeEventListener('resize', this.boundRecalculateTravel)
    window.removeEventListener('orientationchange', this.boundRecalculateTravel)
    window.visualViewport?.removeEventListener('resize', this.boundRecalculateTravel)
    document.removeEventListener('turbo:before-render', this.boundStartTransition)
    document.removeEventListener('turbo:render', this.boundEndTransition)
    document.removeEventListener('turbo:load', this.boundEndTransition)

    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  handleScroll() {
    if (this.isTransitioning) return
    if (this.lockValue) return


    const currentScrollY = this.currentScrollY()
    const delta = currentScrollY - this.lastScrollY

    if (currentScrollY <= this.thresholdValue) {
      this.progress = 0
      this.lastScrollY = currentScrollY
      this.queueRender()
      return
    }

    if (Math.abs(delta) < 0.5) {
      return
    }

    this.progress = this.clamp(this.progress + (delta / this.travelPx) * this.sensitivityValue, 0, 1)

    this.lastScrollY = currentScrollY
    this.queueRender()
  }

  recalculateTravel() {
    if (this.isTransitioning) return

    const height = this.hasPanelTarget ? this.panelTarget.getBoundingClientRect().height : this.element.getBoundingClientRect().height
    this.travelPx = Math.max(height + 8, 1)
    this.progress = this.clamp(this.progress, 0, 1)
    this.queueRender()
  }

  startTransition() {
    this.isTransitioning = true
  }

  endTransition() {
    this.isTransitioning = false
    if (this.lockValue) this.progress = 0
    this.lastScrollY = this.currentScrollY()
    this.recalculateTravel()
  }

  queueRender() {
    this.needsRender = true

    if (this.rafId) {
      return
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null

      if (!this.needsRender) {
        return
      }

      this.needsRender = false
      this.render()
    })
  }

  render() {
    const panelTranslateY = Math.round(this.progress * this.travelPx)
    const panel = this.hasPanelTarget ? this.panelTarget : this.element
    panel.style.transform = `translate3d(0, ${panelTranslateY}px, 0)`
    panel.style.pointerEvents = this.progress >= 0.99 ? 'none' : 'auto'

    if (!this.hasCreateButtonTarget) {
      return
    }

    const createButtonProgress = this.clamp(Math.min(this.progress, this.createStopValue), 0, 1)
    const buttonTranslateY = Math.round(createButtonProgress * this.travelPx)
    this.createButtonTarget.style.transform = `translate3d(0, ${buttonTranslateY}px, 0)`
    this.createButtonTarget.style.pointerEvents = createButtonProgress >= 0.99 ? 'none' : 'auto'
  }

  currentScrollY() {
    return Math.max(window.scrollY, 0)
  }

  clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
  }
}
